import { useTranslation } from "react-i18next";
import cx from "classnames";
import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { JourneyFormTab } from "./JourneyFormTab";
import { useNavigate } from "react-router-dom";
import managerDashboardApi from "../../api/dashboard/managerDashboardApi";
import myDashboardApi from "../../api/dashboard/myDashboardApi";
import dualPrepJourneyHelper from "../../helpers/dualPrepJourneyHelper";
import AppRoutes from "../../routes/AppRoutes";
import { UserContextInterface } from "../../state/UserContext";
import { JourneyFormButtonArg } from "../../types/dashboard/JourneyFormDropDownItems";
import { DashboardJourneyTabType } from "../../types/dtos/dashboards/DashboardJourneyTabType";
import JourneyListingDto from "../../types/dtos/dashboards/manager/listings/journey/JourneyListingDto";
import YourPeopleFormSectionUserDto from "../../types/dtos/dashboards/YourPeopleFormSectionUserDto";
import Tabs, { TabDetails } from "../common/Tabs";

interface JourneyFormSectionProps {
  userContext: UserContextInterface;
  /** Only pass this when viewing the logged in user's own forms */
  loggedInUserCurrentJourneyRef: string | null;
  activeForms: JourneyListingDto[];
  completedForms: JourneyListingDto[];
  searchComponent?: React.ReactNode | undefined;
  // Properties only set from places IsYourPeoplePage is true
  managedUser?: YourPeopleFormSectionUserDto | null;
}

const JourneyFormSection = ({
  userContext,
  loggedInUserCurrentJourneyRef,
  activeForms,
  completedForms,
  managedUser,
  searchComponent = undefined,
}: JourneyFormSectionProps) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const dashboardApi = new myDashboardApi(auth.user?.access_token);
  const manDashboardApi = new managerDashboardApi(auth.user?.access_token);
  const clientColour = userContext.user.client.primaryHexColour;

  const [activeTab, setActiveTab] = useState<DashboardJourneyTabType>("ACTIVE");

  const onActiveTabChange = (tabName: string) => {
    let tab: DashboardJourneyTabType = "ACTIVE";
    switch (tabName) {
      case t("Common.Active"):
        tab = "ACTIVE";
        break;
      case t("Common.Completed"):
        tab = "COMPLETED";
        break;
    }
    setActiveTab(tab);
  };

  const handleUpdateClick = (form: JourneyListingDto) => {
    const goToDashboard =
      form.latestAnswerSet?.guidId == null ||
      form.latestAnswerSet?.statusEnum === "PARTIALLY-SAVED-JOURNEY";
    if (goToDashboard) {
      setCurrentJourneyAndRedirectToDashboard(form.journeyReference);
    } else {
      navigateToCollabDoc(form.latestAnswerSet.guidId);
    }
  };

  const handleViewClick = (form: JourneyListingDto) => {
    navigateToCollabDoc(form.latestAnswerSet?.guidId);
  };

  const handleDualPrepItemClick = (
    form: JourneyListingDto,
    arg: JourneyFormButtonArg,
  ) => {
    switch (arg) {
      case "START-EMP-PLANNING":
        setCurrentJourneyAndRedirectToDashboard(form.journeyReference);
        break;
      case "COMPLETE-EMP-PLANNING":
        const empPlanningAnswerSet = form.answerSets.find(
          (x) => x.isDualPrepSubjectAnswerSet,
        );
        const isEmpPlanningPartiallySaved =
          empPlanningAnswerSet?.statusEnum === "PARTIALLY-SAVED-JOURNEY";
        if (
          empPlanningAnswerSet?.guidId == null ||
          isEmpPlanningPartiallySaved
        ) {
          setCurrentJourneyAndRedirectToDashboard(form.journeyReference);
        } else {
          navigateToCollabDoc(empPlanningAnswerSet?.guidId);
        }
        break;
      case "VIEW-EMP-PLANNING":
        const empPlanning = form.answerSets.find(
          (x) => x.isDualPrepSubjectAnswerSet,
        );
        navigateToCollabDoc(empPlanning?.guidId);
        break;
      case "UPDATE-MEETING":
      case "VIEW-MEETING":
        const meetingDoc = form.answerSets.find(
          (x) => x.isDualPrepMeetingDocAnswerSet,
        );
        navigateToCollabDoc(meetingDoc?.guidId);
        break;
    }
  };

  const navigateToCollabDoc = (guidId?: string) => {
    if (!guidId) return null;

    const redirectUrl = AppRoutes.collaborativeDocument.generateRoute(
      guidId,
      false,
      null,
    );
    navigate(redirectUrl);
  };

  const setCurrentJourneyAndRedirectToDashboard = (journeyRef: string) => {
    if (journeyRef === loggedInUserCurrentJourneyRef) {
      navigate(AppRoutes.yourJourney.root);
      return;
    }

    dashboardApi.assignDifferentJourneyToUser(
      null,
      journeyRef,
      (data) => {
        navigate(AppRoutes.yourJourney.root);
      },
      (error) => {
        console.error("Error setting current journey", error);
      },
    );
  };

  let tabs: Array<TabDetails> = [
    {
      title: t("Common.Active"),
      content: (
        <JourneyFormTab
          userContext={userContext}
          forms={activeForms}
          selectedTab={activeTab}
          onUpdateClick={handleUpdateClick}
          onViewClick={handleViewClick}
          onDualPrepItemClick={handleDualPrepItemClick}
          loggedInUserCurrentJourneyRef={loggedInUserCurrentJourneyRef}
        />
      ),
      displayAlertIcon: false,
      itemCount: activeForms.length,
      itemCountDisplayMode: "BRACKETS",
    },
    {
      title: t("Common.Completed"),
      content: (
        <JourneyFormTab
          userContext={userContext}
          forms={completedForms}
          selectedTab={activeTab}
          onUpdateClick={handleUpdateClick}
          onViewClick={handleViewClick}
          onDualPrepItemClick={handleDualPrepItemClick}
          loggedInUserCurrentJourneyRef={loggedInUserCurrentJourneyRef}
        />
      ),
      displayAlertIcon: false,
      itemCount: completedForms.length,
      itemCountDisplayMode: "BRACKETS",
    },
  ];

  return (
    <div>
      <Tabs
        tabs={tabs}
        selectedTabClassNames={cx(
          "radix-state-inactive:border-0 radix-state-inactive:hover:border-b-2 radix-state-inactive:hover:!border-b-gray-200",
          "radix-state-active:border-b-2",
          clientColour
            ? `radix-state-active:border-b-[#${clientColour}]`
            : "radix-state-active:border-b-gray-400",
          clientColour ? `radix-state-active:text-[#${clientColour}]` : "",
        )}
        maxTabsOnSmallScreens={3}
        contextClassNames="!py-0"
        tabListWidth="w-1/3"
        onActiveTabChangeEvent={onActiveTabChange}
        customComponentOnRightOfTabs={searchComponent}
      />
    </div>
  );
};

export default JourneyFormSection;
